<template>
  <Overlay
    type="dialog"
    :open="open"
    :label="
      appointmentsStore.selectedAppointment
        ? $t('appointments.entry.title_update')
        : $t('appointments.entry.title')
    "
    :style="`--width: 800px`"
    @request-close="onClose"
  >
    <div
      class="entry"
      :class="{
        'entry--full': !selectedService?._viuManualTimeslot
      }"
    >
      <div class="entry__fields">
        <FormSelect
          v-model="companyModel"
          :disabled="Boolean(appointmentsStore.selectedAppointment)"
          :label="$t('appointments.entry.store.label')"
          :placeholder="$t('globals.pleaseSelect')"
          :options="companiesOptions"
          class="entry__field"
          @on-select="(value) => (companyModel = value)"
        />
        <FormSelect
          v-model="serviceModel"
          :disabled="Boolean(appointmentsStore.selectedAppointment)"
          :label="$t('appointments.entry.type.label')"
          :placeholder="$t('globals.pleaseSelect')"
          :options="servicesOptions"
          class="entry__field"
          @on-select="(value) => (serviceModel = value)"
        />
      </div>
      <div v-if="selectedService?._viuMandatoryEmployee" class="entry__fields">
        <FormSelect
          v-model="employeeModel"
          :label="$t('appointments.entry.employee.label')"
          :placeholder="$t('globals.pleaseSelect')"
          :options="employeeOptions"
          class="entry__field"
          @on-select="(value) => (employeeModel = value)"
        />
        <FormInput
          v-if="selectedService?._viuManualTimeslot"
          v-model="manualTimeslotModel"
          type="datetime-local"
          :min="minManualTimeslot"
          :max="maxManualTimeslot"
          :label="$t('appointments.entry.timeslot.label')"
          class="entry__field"
          @click="(event) => event.target.showPicker()"
        />
        <div v-else class="entry__field" />
      </div>
      <div v-if="false" class="entry__time-frame">
        <FormSelect
          v-model="timeFrameModel"
          :label="$t('appointments.entry.time_frame.label')"
          :placeholder="$t('globals.pleaseSelect')"
          :options="timeFrameOptions"
          @on-select="(value) => (timeFrameModel = value)"
        />
      </div>
      <sl-spinner v-if="!timeslotsLoaded" class="entry__fetching" />
      <div v-else-if="!selectedService?._viuManualTimeslot" class="entry__picker">
        <sl-button
          variant="default"
          :disabled="timeslotOffset === 0"
          @click="timeslotOffset = Math.max(0, timeslotOffset - 5)"
        >
          <PhArrowLeft slot="prefix" :size="20" />
        </sl-button>
        <div class="entry__days">
          <div v-for="slot in filteredTimeslots" :key="slot.day" class="entry__day">
            <span
              class="entry__date"
              :class="{
                'entry__date--current': isToday(slot.date)
              }"
            >
              <span>
                {{ slot.dayEstimate }}
              </span>
              <span class="entry__date__weekday">
                {{ slot.weekday }}
              </span>
              <span>
                {{ format(slot.date, 'dd.MM') }}
              </span>
            </span>
            <div class="entry__slots">
              <sl-button
                v-for="time in slot.times"
                :key="time.time"
                :variant="selectedDay === slot.day && selectedSlot === time.time ? 'primary' : 'default'"
                :class="['entry__slot', { 'entry__slot--remote': isTestEnv && time.type === 'remote' }]"
                @click="pickSlot(slot.day, time)"
              >
                {{ time.time }}
              </sl-button>
            </div>
          </div>
        </div>
        <sl-button
          variant="default"
          :disabled="timeslotOffset + 5 >= timeslots?.length"
          @click="timeslotOffset = Math.min(timeslots?.length, timeslotOffset + 5)"
        >
          <PhArrowRight slot="prefix" :size="20" />
        </sl-button>
      </div>
      <div v-if="formattedDate" class="entry__summary">
        <div>
          <div>{{ appointmentsStore.appointmentTitleFixByService(selectedService.key) }}</div>
          <p v-if="selectedEmployee">
            {{ selectedEmployee.name }}
          </p>
          <p
            v-if="Boolean(appointmentsStore.selectedAppointment) && timeslotUpdated"
            class="entry__summary__date entry__summary__date--updated"
          >
            {{ formattedInitialDate }}
          </p>
          <p v-if="formattedDate" class="entry__summary__date">
            {{ formattedDate }}
          </p>
        </div>
        <sl-button
          v-if="!appointmentsStore.selectedAppointment"
          variant="default"
          size="small"
          circle
          class="entry__clear"
          @click="resetTimeslot"
        >
          <sl-icon name="x-lg" />
        </sl-button>
      </div>
      <div v-if="customerEmail" class="entry__summary">
        <div>
          <div>{{ $t(`appointments.entry.customer_label`) }}</div>
          <p v-if="customerName">
            {{ customerName }}
          </p>
          <div class="entry__personal">
            <p>{{ customerEmail }}</p>
            <p v-if="customerPhone">
              {{ customerPhone }}
            </p>
            <p v-else style="color: #ef4444">
              {{ $t('appointments.entry.no_phone') }}
            </p>
          </div>
        </div>

        <div v-if="!appointmentsStore.selectedAppointment" class="entry__edit">
          <sl-button variant="default" @click="onEditCustomer">
            {{ $t('globals.edit') }}
          </sl-button>
        </div>

        <sl-button
          v-if="!appointmentsStore.selectedAppointment"
          variant="default"
          size="small"
          class="entry__clear"
          circle
          @click="resetCustomer"
        >
          <sl-icon name="x-lg" />
        </sl-button>
      </div>
      <template v-if="!customerEmail && selectedService?._viuCustomerRequired">
        <div
          style="
            margin-top: var(--sl-spacing-small);
            display: flex;
            gap: var(--sl-spacing-small);
            justify-content: start;
            align-content: center;
            align-items: center;
            justify-items: center;
          "
        >
          <sl-button variant="primary" @click="onSelectCustomer"
            ><PhUser slot="prefix" :size="20" />{{ $t('customer.select_customer') }}
          </sl-button>

          <sl-button variant="default" @click="onAddNewCustomer"
            ><PhPlus slot="prefix" :size="20" />{{ $t('customer.add_new_customer') }}
          </sl-button>
        </div>
      </template>
      <Notes
        ref="notes"
        :has-submit="false"
      />
      <div
        v-if="selectedService?._viuCustomerRequired"
        class="entry__email-notice"
      >
        {{ $t('appointments.entry.email_notice') }}
      </div>
    </div>
    <div slot="footer" style="width: 100%">
      <div v-if="!canSubmit" style="margin-bottom: 10px">
        <template v-if="selectedService?._viuCustomerRequired">
          {{ $t('appointments.entry.correct_data') }}
        </template>
        <template v-else>
          {{ $t('appointments.entry.correct_data_no_customer') }}
        </template>
      </div>
      <div style="width: 100%; display: flex; justify-content: space-between">
        <sl-button :disabled="inProgress" @click="onClose">
          {{ $t('globals.cancel') }}
        </sl-button>
        <sl-button
          variant="primary"
          :disabled="!canSubmit || inProgress || !timeslotUpdated"
          :loading="inProgress"
          @click="onConfirm"
        >
          {{ $t('appointments.entry.confirm') }}
        </sl-button>
      </div>
    </div>
  </Overlay>
  <Overlay
    type="dialog"
    :open="customerSearchOpen"
    :label="$t('appointments.customer_search.title')"
    @request-close="closeCustomerSearch"
  >
    <div>
      <sl-input
        v-model="query"
        :label="$t('appointments.customer_search.input.label')"
        size="medium"
        clearable
        autocomplete="off"
      >
        <sl-icon slot="prefix" name="search"></sl-icon>
      </sl-input>
      <div v-if="customerSearchResults" class="entry__search-customers">
        <div
          v-for="customer in customerSearchResults.slice(0, 5)"
          :key="customer.id"
          class="entry__search-customer"
        >
          <div class="entry__search-customer__name">
            <div>{{ [customer?.first_name, customer?.last_name].filter(Boolean).join(' ') }}</div>
            <div class="text-color-neutral">{{ customer.email }}</div>
          </div>
          <sl-button
            :variant="customerPicked?.id === customer.id ? 'primary' : 'default'"
            size="small"
            @click="customerPicked = customer"
          >
            <template v-if="customerPicked?.id === customer.id">
              {{ $t('globals.selected') }}
            </template>
            <template v-else>
              {{ $t('globals.select') }}
            </template>
          </sl-button>
        </div>
      </div>
    </div>
    <div slot="footer" style="width: 100%">
      <div v-if="!canSelectCustomer" style="margin-bottom: 10px">
        {{ $t('appointments.customer_search.correct_data') }}
      </div>
      <div style="width: 100%; display: flex; justify-content: space-between">
        <sl-button :disabled="inProgress" @click="closeCustomerSearch">
          {{ $t('globals.cancel') }}
        </sl-button>
        <sl-button variant="primary" :disabled="!canSelectCustomer" @click="onCustomerConfirm">
          {{ $t('appointments.customer_search.confirm') }}
        </sl-button>
      </div>
    </div>
  </Overlay>
  <Overlay :open="unbookableOpen" type="dialog" no-header @request-close="unbookableOpen = false">
    <div
      style="
        display: flex;
        flex-direction: column;
        gap: 16px;
        text-align: center;
        padding-top: 38px;
      "
    >
      <div
        style="
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 11px;
          color: #f87171;
        "
      >
        <PhCalendarX size="32" />
        <h4 style="font-size: 24px; line-height: 34px">
          {{ $t('appointments.entry.unbookable.title') }}
        </h4>
      </div>
      <p>{{ $t('appointments.entry.unbookable.description') }}</p>
    </div>
    <div slot="footer" style="width: 100%; display: flex; justify-content: center">
      <sl-button variant="primary" @click="unbookableOpen = false">
        {{ $t('globals.back') }}
      </sl-button>
    </div>
  </Overlay>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { PhPlus, PhUser, PhArrowLeft, PhArrowRight, PhCalendarX } from '@phosphor-icons/vue'
import _slugify from 'slugify'

import FormSelect from '@/components/Form/FormSelect/FormSelect.vue'
import FormInput from '@/components/Form/FormInput/FormInput.vue'
import Overlay from '@/components/shared/Overlay/Overlay.vue'
import Notes from '@/components/shared/Notes/Notes.vue'

import { format, isToday } from 'date-fns'
import { fromZonedTime } from 'date-fns-tz'

import { useI18n } from 'vue-i18n'
import { useTypesense } from '@/composables/useTypesense'
import { useTimify } from '@/composables/useTimify'
import { useUiStore } from '@/stores/ui.js'
import { useCustomerStore } from '@/stores/customer.js'
import { useCartStore } from '@/stores/cart.js'
import { useNotification } from '@/composables/useNotification.js'
import { useCustomer } from '@/composables/useCustomer.js'
import { watchDebounced } from '@vueuse/core'
import { useDeviceStore } from '@/stores/device.js'
import { useWeekday } from '@/composables/useWeekday.js'
import { useDayEstimate } from '@/composables/useDayEstimate.js'
import { useAppointmentsStore } from '@/stores/appointments'
import { useNow } from '@/composables/useNow'
import { useFormatPhoneNumber } from '@/composables/useFormatPhoneNumber'
import { useNotes } from '@/composables/useNotes'

const emit = defineEmits(['updated'])

const props = defineProps({
  currentCompany: {
    type: Object,
    default() {
      return {}
    }
  },
  companies: {
    type: Array,
    default() {
      return []
    }
  },
  appointmentInEdit: {
    type: Object,
    default: undefined
  },
  servicesList: {
    type: Array,
    default() {
      return []
    }
  },
  resourcesList: {
    type: Array,
    default() {
      return []
    }
  }
})

const { t, te, locale } = useI18n()
const timify = useTimify()
const uiStore = useUiStore()
const customerStore = useCustomerStore()
const { loadCustomerById } = useCustomer()
const cartStore = useCartStore()
const notification = useNotification()
const typesense = useTypesense()
const deviceStore = useDeviceStore()
const appointmentsStore = useAppointmentsStore()
const now = useNow(1000 * 60 * 60)
const { createCustomerNote } = useNotes()
const isTestEnv = import.meta.env.MODE !== 'production'

const currentLocale = computed(() => {
  let currentLocale = locale.value
  if (currentLocale.includes('de')) {
    currentLocale = 'de-de'
  } else if (currentLocale.includes('en')) {
    currentLocale = 'en-gb'
  } else if (currentLocale.includes('fr')) {
    currentLocale = 'fr-fr'
  }

  return currentLocale
})

const notes = ref(null)
const open = defineModel('open', {
  type: Boolean,
  default: false
})

function close() {
  open.value = false
}

function onClose() {
  close()
  reset()
}

function roundToNextFifthMinute(input) {
  const date = new Date(input)
  const minutes = date.getMinutes()
  const roundedMinutes = Math.ceil(minutes / 5) * 5
  date.setMinutes(roundedMinutes)
  return date
}

// pick timeframe to show
const timeFrameOptions = computed(() => {
  return [
    {
      value: '',
      text: t('appointments.entry.time_frame.all')
    },
    {
      value: 'before_noon',
      text: t('appointments.entry.time_frame.before_noon')
    },
    {
      value: 'after_noon',
      text: t('appointments.entry.time_frame.after_noon')
    }
  ]
})
const timeFrameModel = ref(timeFrameOptions.value[0]?.value)
const timeFrame = computed(() => {
  if (timeFrameModel.value === 'before_noon') {
    return ['00:00', '12:00']
  } else if (timeFrameModel.value === 'after_noon') {
    return ['12:00', '23:59']
  }
  return ['00:00', '23:59']
})

// models
const serviceModel = ref()
const employeeModel = ref()
const currentDayModel = ref(new Date())
const manualTimeslotModel = ref(null)
const companyModel = ref(props.currentCompany?.id)

const selectedService = computed(() => {
  const item = services.value.find((service) => service.id === serviceModel.value) || {}
  return {
    ...item,
    // could use isBookable everywhere, but not sure this will be true for all services
    _viuMandatoryEmployee: !item.isBookable,
    _viuNotifyCustomers: Boolean(item.isBookable),
    _viuManualTimeslot: !item.isBookable,
    _viuCustomerRequired: ![
      'store_admin_25_min',
      'store_admin_30_min',
      'store_admin_60_min',
      'mounting',
    ].includes(item.key),
    filter(resource) {
      if (['eyetest_walk_in'].includes(item.key)) {
        return resource.category?.externalId === 'opticians'
      }
      return true
    }
  }
})

const eyeTestSlugs = [
  'sehtest',
  'sehtest_mit_anschliessender_beratung',
  'kostenloser_sehtest_ch',
  'kostenloser_sehtest_mit_anschliessender_beratung_ch',
  'kontaktlinsen_anpassung',
  'kostenloser_sehtest',
  'kostenloser_sehtest_mit_anschliessender_beratung',
]

const eyeTestAlternativesMap = {
  'kostenloser_sehtest': 'remote_kostenloser_sehtest',
  'kostenloser_sehtest_mit_anschliessender_beratung': 'remote_kostenloser_sehtest_mit_anschliessender_beratung',
  'sehtest': 'remote_sehtest',
  'sehtest_mit_anschliessender_beratung': 'remote_sehtest_mit_anschliessender_beratung',
}

const serviceOrder = {
  ...eyeTestSlugs.reduce((acc, slug, index, all) => {
    return {
      ...acc,
      [slug]: -1 * all.length + index
    }
  }, {}),
  sehtest_fuer_2: 15,
  beratung: 20,
  abholung_service: 30,
  fuehrerscheinsehtest: 40,
  eyetest_walk_in: 50,
  reklamation: 60,
  store_admin_25_min: 70,
  store_admin_30_min: 70,
  store_admin_60_min: 80,
  mounting: 90
}

const loadedServices = ref()
watch(
  () => props.servicesList,
  (value) => {
    loadedServices.value = value
  },
  { immediate: true }
)
watch(companyModel, async (value) => {
  try {
    const { data } = await timify.services(value)
    loadedServices.value = data
  } catch (error) {
    appointmentsStore.displayNetworkError()
  }
})

function serviceCanBeDisplayed(service) {
  return !['sehtest_fuer_2'].includes(service.key)
}

const services = computed(() => {
  return loadedServices.value
    // hack until timify fixes
    .filter(service => {
      if (deviceStore.settings.country !== 'ch') {
        return service.price === 0
      }
      return true
    })
    .map((service) => {
      const key = slugify(service.name)
      return {
        ...service,
        key,
        order: serviceOrder[key],
        _viuInternalService: !service.isBookable
      }
    })
    .filter((service) => {
      const isDisabledEyeTest = (eyeTestSlugs.includes(service.key) && !service.isBookable) ||
        [
          'kostenloser_sehtest_ch',
          'kostenloser_sehtest_mit_anschliessender_beratung_ch'
        ].includes(service.key)
      return !service.key.includes('acuity') &&
        !isDisabledEyeTest &&
        te(`appointments.services.${service.key}`) &&
        serviceCanBeDisplayed(service)
    })
    .sort((a, b) => a.order - b.order)
})

const servicesOptions = computed(() => {
  return services.value.map((service, index, all) => {
    const name = appointmentsStore.appointmentTitleFixByService(service.key)
    return {
      value: service.id,
      text: t('appointments.entry.service_with_duration', { name, duration: service.duration }),
      dividerTop:
        index !== 0 && all[index - 1]?._viuInternalService !== service._viuInternalService
    }
  })
})

watch([() => appointmentsStore.selectedAppointmentDateTimeModel, selectedService], ([dateTime, selectedService]) => {
  if (dateTime && selectedService?._viuManualTimeslot) {
    manualTimeslotModel.value = dateTime
  } else {
    manualTimeslotModel.value = null
  }
})

const selectedCompany = computed(() => {
  return props.companies.find((company) => company.id === companyModel.value)
})

const companiesOptions = computed(() => {
  return props.companies
    .map((company) => ({
      value: company.id,
      text: company.name.replace('VIU Eyewear', '').trim()
    }))
    .sort((a, b) => a.text.localeCompare(b.text))
})

const resources = ref()
watch(
  () => props.resourcesList,
  (value) => {
    resources.value = value
  },
  { immediate: true }
)
watch(companyModel, async (value) => {
  try {
    const { data } = await timify.resources(value)
    resources.value = data
  } catch {
    appointmentsStore.displayNetworkError()
  }
})

const customerFields = ref([])
watch(
  companyModel,
  async (value) => {
    try {
      const { data } = await timify.customerFields(value)
      customerFields.value = data
    } catch {
      appointmentsStore.displayNetworkError()
    }
  },
  { immediate: true }
)

const employees = computed(() => {
  return resources.value.filter((resource) =>
    ['team', 'opticians'].includes(resource.category?.externalId)
  )
})

const employeeOptions = computed(() => {
  try {
    return employees.value.filter(selectedService.value.filter).map((resource) => ({
      value: resource.id,
      text: resource.name
    }))
  } catch (error) {
    console.error(error)
    return []
  }
})

watch(employeeOptions, (value) => {
  if (appointmentsStore.selectedAppointment) {
    employeeModel.value = appointmentsStore.selectedAppointment.resources?.find((resource) => Boolean(resource?.externalId))?.id
  } else {
    const userEmployee = employees.value.find((employee) => {
      return deviceStore.settings.currentEmployee.eid === employee.externalId
    })
    // current user or first user in the list
    employeeModel.value = userEmployee?.id || value[0]?.value
  }
})

watch(serviceModel, () => {
  if (!appointmentsStore.selectedAppointment) {
    resetTimeslot()
  }
})

watch(
  () => appointmentsStore.selectedAppointment,
  (value) => {
    if (value) {
      // For combination appointments, service.id is just the first sub-event's service.
      // Use combination.serviceId to identify the full service type (e.g. 'remote_kostenloser_sehtest_mit_anschliessender_beratung').
      // If no combination, fall back to the individual service.id.
      const serviceIdToMap = value.combination?.serviceId || value.service?.id
      const serviceFromAppointment = loadedServices.value?.find(s => s.id === serviceIdToMap)
      const serviceKey = serviceFromAppointment ? slugify(serviceFromAppointment.name) : null
      const localKeyForRemote = Object.keys(eyeTestAlternativesMap).find(
        k => eyeTestAlternativesMap[k] === serviceKey
      )
      if (localKeyForRemote) {
        const localService = loadedServices.value?.find(s => slugify(s.name) === localKeyForRemote)
        serviceModel.value = localService?.id || serviceIdToMap
      } else {
        serviceModel.value = serviceIdToMap
      }
      employeeModel.value = value.resources?.find((resource) => Boolean(resource?.externalId))?.id
    } else {
      serviceModel.value = servicesOptions.value[0]?.value
      employeeModel.value = employeeOptions.value[0]?.value
    }
  },
  { immediate: true }
)

const minManualTimeslot = computed(() => {
  return format(roundToNextFifthMinute(new Date()), "yyyy-MM-dd'T'HH:mm")
})

const maxManualTimeslot = computed(() => {
  const now = new Date()
  if (selectedService.value?.key === 'eyetest_walk_in') {
    now.setHours(23, 59)
  } else {
    now.setDate(now.getDate() + 27)
  }
  return format(now, "yyyy-MM-dd'T'HH:mm")
})

const selectedEmployee = computed(() => {
  if (selectedService.value?._viuMandatoryEmployee) {
    return employees.value.find((employee) => employee.id === employeeModel.value)
  }
  return undefined
})

const resourceModel = computed(() => {
  // if employee selected
  if (employeeModel.value && selectedService.value._viuMandatoryEmployee) {
    return [employeeModel.value]
  }
  return employeeOptions.value.map((item) => item.value).filter(Boolean)
})

watch(services, () => {
  // resets services on company change
  // all service ids are different
  serviceModel.value = services.value[0]?.id
})

const remoteAlternativeService = computed(() => {
  const remoteKey = eyeTestAlternativesMap[selectedService.value?.key]
  if (!remoteKey || !loadedServices.value) return null
  return loadedServices.value.find(s => slugify(s.name) === remoteKey) || null
})

const timeslots = ref([])
const timeslotsLoaded = ref(false)

const timeslotOffset = ref(0)
watch(timeslots, () => {
  timeslotOffset.value = 0
})

const filteredTimeslots = computed(() => {
  return timeslots.value
    .filter((timeslot) => {
      if (selectedService.value?.key === 'sehtest_fuer_2') {
        const current = new Date(timeslot.day)
        const start = new Date()
        start.setDate(7)
        start.setMonth(1)
        start.setHours(0, 0, 0, 0)
        const end = new Date()
        end.setDate(22)
        end.setMonth(1)
        end.setHours(23, 59, 59, 999)
        return current > start && current < end
      }
      return true
    })
    .slice(timeslotOffset.value, timeslotOffset.value + 5)
    .map((timeslot) => {
      const date = new Date(timeslot.day)
      return {
        ...timeslot,
        date,
        weekday: useWeekday(date, t).value,
        dayEstimate: useDayEstimate(date, t).value,
        times: timeslot.times.filter((timeObj) => {
          // we get times in 'current store' timezone. they are all CET
          return !timeFrameModel.value || (timeObj.time > timeFrame.value[0] && timeObj.time < timeFrame.value[1])
        })
      }
    })
})

watch(
  [selectedService, resourceModel],
  async ([service, resourceId]) => {
    if (!service?.id || !resourceId) return

    if (service._viuManualTimeslot) {
      timeslots.value = []
      return
    }

    // fetch available appointments
    try {
      timeslotsLoaded.value = false
      const remoteService = remoteAlternativeService.value

      const fetchLocal = timify.availability({
        companyId: selectedCompany.value.id,
        serviceId: service.id,
        day: currentDayModel.value,
        numberOfDays: 27
      })
      const fetchRemote = remoteService
        ? timify.availability({
            companyId: selectedCompany.value.id,
            serviceId: remoteService.id,
            day: currentDayModel.value,
            numberOfDays: 27
          })
        : Promise.resolve(null)

      const [localResponse, remoteResponse] = await Promise.all([fetchLocal, fetchRemote])

      const localSlots = (localResponse.data?.slots || [])
        .filter(slot => slot.times?.length)
        .map(slot => ({
          ...slot,
          times: slot.times.map(time => ({ time, serviceId: service.id, type: 'local' }))
        }))

      if (!remoteService || !remoteResponse) {
        timeslots.value = localSlots
      } else {
        const remoteSlots = (remoteResponse.data?.slots || [])
          .filter(slot => slot.times?.length)
          .map(slot => ({
            ...slot,
            times: slot.times.map(time => ({ time, serviceId: remoteService.id, type: 'remote' }))
          }))

        // merge: remote (value) has priority over local for the same time
        const allDays = new Set([...localSlots.map(s => s.day), ...remoteSlots.map(s => s.day)])
        timeslots.value = Array.from(allDays).sort().map(day => {
          const local = localSlots.find(s => s.day === day)
          const remote = remoteSlots.find(s => s.day === day)
          const timeMap = {}
          for (const t of (local?.times || [])) timeMap[t.time] = t
          for (const t of (remote?.times || [])) timeMap[t.time] = t
          const times = Object.values(timeMap).sort((a, b) => a.time.localeCompare(b.time))
          return { day, times }
        }).filter(s => s.times.length)
      }
    } catch (error) {
      timeslots.value = []
      notification.showNotification({
        message: t('appointments.entry.errors.availabilities_fetch_failed'),
        variant: 'danger'
      })
      console.error(error)
    } finally {
      timeslotsLoaded.value = true
    }
  },
  { immediate: true }
)

// pick timeslot
const editDate = computed(() => {
  return appointmentsStore.selectedAppointment?.start
})
const editDay = computed(() => {
  if (!editDate.value) {
    return
  }
  return format(editDate.value, 'yyyy-MM-dd')
})
const editSlot = computed(() => {
  if (!editDate.value) {
    return
  }
  return format(editDate.value, 'HH:mm')
})

const selectedDay = ref()
watch(editDay, (value) => {
  selectedDay.value = value
})

const selectedSlot = ref(null)
const selectedSlotServiceId = ref(null)
watch(editSlot, (value) => {
  selectedSlot.value = value
})

const timeslotUpdated = computed(() => {
  return editDay.value !== selectedDay.value ||
    editSlot.value !== selectedSlot.value ||
    manualTimeslotModel.value !== appointmentsStore.selectedAppointmentDateTimeModel
})

function resetTimeslot() {
  selectedDay.value = null
  selectedSlot.value = null
  selectedSlotServiceId.value = null
}

function resetCustomer() {
  cartStore.$reset()
  customerStore.reset()
}

function reset() {
  resetTimeslot()
  resetCustomer()
}

// need to be careful what the user settings are
// must be in the same timezone as the company, if we just init with new Date() it will be god knows what
const dateInStoreTz = computed(() => {
  if ((!selectedDay.value || !selectedSlot.value) && !manualTimeslotModel.value) {
    return
  }
  if (manualTimeslotModel.value) {
    return fromZonedTime(new Date(manualTimeslotModel.value), 'Europe/Berlin')
  }
  return fromZonedTime(new Date(`${selectedDay.value} ${selectedSlot.value}`), 'Europe/Berlin')
})

const formatter = computed(() => {
  return new Intl.DateTimeFormat(currentLocale.value, {
    dateStyle: 'full',
    timeStyle: 'short'
  })
})

const formattedDate = computed(() => {
  if (!dateInStoreTz.value) {
    return
  }
  return formatter.value.format(dateInStoreTz.value)
})

const formattedInitialDate = computed(() => {
  if (!editDate.value) {
    return
  }
  return formatter.value.format(roundToNextFifthMinute(editDate.value))
})

function pickSlot(day, timeObj) {
  selectedDay.value = day
  selectedSlot.value = timeObj.time
  selectedSlotServiceId.value = timeObj.serviceId
}

// customer
const customerSearchOpen = ref(false)
const query = ref('')
const customerSearchResults = ref([])
const customerPicked = ref(null)

const canSelectCustomer = computed(() => {
  return Boolean(customerPicked.value)
})

function closeCustomerSearch() {
  customerSearchOpen.value = false
}

async function onCustomerConfirm() {
  if (canSelectCustomer.value) {
    customerStore.reset()
    const customer = await loadCustomerById(customerPicked.value.id)
    if (customer) {
      customerStore.setCustomer(customer)
      closeCustomerSearch()
    }
  }
}

watchDebounced(
  query,
  async (value) => {
    if (value?.length > 2) {
      const response = await typesense
        .client()
        .collections(import.meta.env.VITE_TYPESENSE_CUSTOMERS_COLLECTION)
        .documents()
        .search({
          q: query.value,
          query_by:
            'first_name,last_name,email,phone,address_line_1,address_line_2,address_zip_code,address_city',
          sort_by: `_text_match:desc`,
          limit: 50,
          include_fields: `id,first_name,last_name,email,phone,gender,language,birth_date,status,tags,restrictions,address_line_1,address_line_2,address_zip_code,address_city,country,reference`
        })
      customerSearchResults.value = (response.hits || []).map((hit) => {
        return hit.document
      })
    }
  },
  { debounce: 700, maxWait: 4000 }
)

function onSelectCustomer() {
  customerSearchOpen.value = true
}

function onAddNewCustomer() {
  close()
  uiStore.$patch({
    showCustomerCreationEditDialog: true,
    showCustomerSearchDialog: false
  })
}

function onEditCustomer() {
  close()
  uiStore.$patch({ currentCustomerDrawer: 'profile' })
}

// confirm
const customerInEdit = computed(() => {
  return appointmentsStore.selectedAppointment?.singleCustomers?.[0]
})
const customerEmail = computed(() => {
  return customerStore.customer?.email || customerInEdit.value?.email
})
const customerName = computed(() => {
  return (
    [customerStore.customer?.metadata?.first_name, customerStore.customer?.metadata?.last_name]
      .filter(Boolean)
      .join(' ') || customerInEdit.value?.fullName
  )
})
const customerPhone = computed(() => {
  return customerStore.customer?.metadata?.phone || customerInEdit.value?.phone?.phone
})

const inProgress = ref(false)
const canSubmit = computed(() => {
  return Boolean(
    dateInStoreTz.value &&
      ((customerEmail.value && customerPhone.value) || !selectedService.value?._viuCustomerRequired)
  )
})

// the data passed must be UTC (timify wants it like this) so we must convert to that
// timify says it wants ISO 8601, which you see if you try to pass ISO 8601 and get an error
// instead it wants YYYY-MM-DD HH:MM just in UTC timzone. motherfucking fuck
function timifyDatetimeFormat(date) {
  const [day, fullTime] = date.toISOString().split('T')
  const time = fullTime.split(':').slice(0, 2).join(':')

  return [day, time].join(' ')
}

const roundedTimifyDatetime = computed(() => {
  return timifyDatetimeFormat(roundToNextFifthMinute(dateInStoreTz.value))
})

const fields = computed(() => {
  const { customer } = customerStore

  return customerFields.value.map((field) => {
    let value
    if (field.translationKey === 'customerFields.basic.email') {
      value = customer?.email
    } else if (field.translationKey === 'customerFields.basic.mobilePhone') {
      const country = customer?.metadata?.country || deviceStore.settings.country.toUpperCase()
      const number = useFormatPhoneNumber(customer?.metadata?.phone, country, true)
      value = JSON.stringify({
        number,
        country
      })
    } else if (field.translationKey === 'customerFields.basic.firstName') {
      value = customer?.metadata?.first_name
    } else if (field.translationKey === 'customerFields.basic.lastName') {
      value = customer?.metadata?.last_name
    }
    return {
      id: field.id,
      value
    }
  })
})

const unbookableOpen = ref(false)

async function onConfirm() {
  if (canSubmit.value) {
    let createdAppointment
    const customerId = customerStore.getCustomerId
    inProgress.value = true
    try {
      if (appointmentsStore.selectedAppointment) {
        if (appointmentsStore.selectedAppointment.isOnlineBooking) {
          const originalServiceId = appointmentsStore.selectedAppointment.combination?.serviceId ||
            appointmentsStore.selectedAppointment.serviceId
          const serviceChanged = selectedSlotServiceId.value &&
            selectedSlotServiceId.value !== originalServiceId
          console.log('serviceChanged', {
            serviceChanged,
            selectedSlotServiceId: selectedSlotServiceId.value,
            originalServiceId,
          })
          if (serviceChanged) {
            // The new slot belongs to a different service (e.g. local vs remote).
            // Timify does not allow rescheduling across services, so book a new
            // appointment and cancel the old one instead.
            const response = await timify.bookerTimeslot({
              companyId: selectedCompany.value.id,
              serviceId: selectedSlotServiceId.value,
              date: selectedDay.value,
              time: selectedSlot.value
            })
            const confirmResponse = await timify.bookerConfirm({
              companyId: selectedCompany.value.id,
              reservationId: response.data.id,
              secret: response.data.secret,
              fields: fields.value,
              locale: currentLocale.value
            })
            if (confirmResponse.error) {
              throw new Error('Error booking new appointment')
            }
            await timify.cancel({
              companyId: selectedCompany.value.id,
              appointmentId: appointmentsStore.selectedAppointment.id
            })
          } else {
            const response = await timify.bookerTimeslot({
              companyId: selectedCompany.value.id,
              serviceId: originalServiceId || selectedSlotServiceId.value,
              date: selectedDay.value,
              time: selectedSlot.value
            })
            const confirmResponse = await timify.bookerConfirm({
              companyId: selectedCompany.value.id,
              reservationId: response.data.id,
              secret: response.data.secret,
              rescheduledEventId: appointmentsStore.selectedAppointment?.id,
              rescheduledSecret: appointmentsStore.selectedAppointment?.reschedulingSecret,
            })
            if (confirmResponse.error) {
              throw new Error('Error rescheduling appointment')
            }
          }
        } else {
          await timify.change({
            datetime: roundedTimifyDatetime.value,
            // serviceId: serviceModel.value,
            // customerId: customerInEdit.value?.id,
            appointmentId: appointmentsStore.selectedAppointment?.id,
            companyId: selectedCompany.value.id
          })
        }
        // todo notification
        emit('updated')
        appointmentsStore.modifyDialogOpen = false
        reset()
      } else {
        if (selectedService.value._viuInternalService) {
          let user
          if (selectedService.value._viuCustomerRequired) {
            const response = await timify.user({
              companyId: selectedCompany.value.id,
              email: customerEmail.value
            })
            user = (response.data || [])[0]
            if (!user?.id) {
              const { data } = await timify.userCreate({
                companyId: selectedCompany.value.id,
                fields: fields.value
              })
              user = data
            }
          }
          if (selectedService.value._viuManualTimeslot) {
            const date = new Date(manualTimeslotModel.value)
            const { data } = await timify.timeshifts({
              companyId: selectedCompany.value.id,
              resourceIds: resourceModel.value,
              date
            })
            const [timeshift] = data
            const { workingTimes } = timeshift || {}
            const pickedTime = format(date, 'HH:mm')
            if (
              !workingTimes?.some(
                (workingTime) => workingTime.begin <= pickedTime && workingTime.end >= pickedTime
              )
            ) {
              unbookableOpen.value = true
              return
            }
          }
          try {
            const { data } = await timify.book({
              companyId: selectedCompany.value.id,
              customerId: user?.id,
              serviceId: serviceModel.value,
              resourceIds: resourceModel.value,
              datetime: roundedTimifyDatetime.value
            })
            createdAppointment = data
            emit('updated', data)
            reset()

            notification.showNotification({
              message: t('appointments.entry.success'),
              variant: 'success'
            })
          } catch (error) {
            unbookableOpen.value = true
            return
          }
        } else {
          const response = await timify.bookerTimeslot({
            companyId: selectedCompany.value.id,
            serviceId: selectedSlotServiceId.value || serviceModel.value,
            date: selectedDay.value,
            time: selectedSlot.value
          })
          const confirmResponse = await timify.bookerConfirm({
            companyId: selectedCompany.value.id,
            reservationId: response.data.id,
            secret: response.data.secret,
            fields: fields.value,
            locale: currentLocale.value
          })
          createdAppointment = confirmResponse?.data?.finaliseOnlineEventReservation
          emit('updated', response.data)
          reset()

          notification.showNotification({
            message: t('appointments.entry.success'),
            variant: 'success'
          })
        }
      }
    } catch (error) {
      console.log(error)
      notification.showNotification({
        message: t('appointments.entry.failure'),
        variant: 'danger'
      })
    } finally {
      inProgress.value = false
    }
    if (createdAppointment && customerId && (selectedService.value._viuCustomerRequired || !selectedService.value._viuInternalService)) {
      if (notes.value?.note) {
        await createCustomerNote(customerId, notes.value.note, {
          errorNotify: false,
          errorCatch: true,
          metadata: {
            timify_appointment: {
              id: createdAppointment.id,
              company_id: appointmentsStore.currentCompany?.id
            }
          }
        })
      }
    }
  }
}

function slugify(name, options = {}) {
  return _slugify(name, {
    lower: true,
    remove: /[./\\()]/,
    locale: 'de',
    ...options
  }).replace(/-/g, '_')
}
</script>

<style src="./AppointmentsEntry.css" scoped></style>
